import React, { useState, useMemo, useEffect } from 'react';
import { FiX, FiSearch, FiPlay, FiLoader, FiCheck, FiSlash } from 'react-icons/fi';
import { useFullStoryApi } from './api';
import { useT } from '../../utils/i18n';
import { showError } from './shared';

/** An effect's name in the current language, falling back to the server's English label. */
export const effectLabel = (t, effect) => {
  if (!effect) return t('fullStory.effect.none');
  const key = `fullStory.effect.${effect.id}`;
  return t(key) === key ? effect.label : t(key);
};

const categoryLabel = (t, id, fallback) => {
  const key = `fullStory.effectCategory.${id}`;
  return t(key) === key ? fallback : t(key);
};

/**
 * Choose the effect for one scene: browse or search the catalogue, see when
 * each one fits, and preview a few seconds of this scene with it before
 * applying. Previews are made on the server and kept for the session, so
 * switching back to one already seen plays at once.
 */
export const EffectPicker = ({ story, scene, index, options, onApply, onClose }) => {
  const fullStoryService = useFullStoryApi();
  const t = useT();
  const id = story._id;
  const portrait = story.aspectRatio === '9:16';
  const effects = options.effects || [];
  const categories = options.effectCategories || {};

  const [selected, setSelected] = useState(scene.effect || 'none');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [previews, setPreviews] = useState({});  // effectId → filename
  const [previewing, setPreviewing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const counts = useMemo(() => effects.reduce((acc, e) => ({ ...acc, [e.category]: (acc[e.category] || 0) + 1 }), {}), [effects]);

  const visible = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return effects.filter((e) => {
      if (category && e.category !== category) return false;
      if (!words.length) return true;
      const text = `${e.label} ${effectLabel(t, e)} ${e.hint} ${categories[e.category] || ''}`.toLowerCase();
      return words.every((w) => text.includes(w));
    });
  }, [effects, category, search, categories, t]);

  const current = effects.find((e) => e.id === selected) || null;
  const canPreview = Boolean(scene.image?.filename);

  const preview = async (effectId = selected) => {
    if (!canPreview || previewing) return;
    setPreviewing(effectId);
    try {
      const { filename } = await fullStoryService.previewScene(id, scene._id, { effect: effectId, motion: scene.motion || undefined });
      setPreviews((p) => ({ ...p, [effectId]: filename }));
    } catch (e) {
      showError(e);
    } finally {
      setPreviewing(null);
    }
  };

  const apply = async () => {
    if (selected === (scene.effect || 'none')) return onClose();
    setSaving(true);
    // onApply resolves to null when saving failed (the error is already shown) — stay open then.
    const saved = await onApply(selected);
    setSaving(false);
    if (saved) onClose();
  };

  const shownPreview = previews[selected];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('fullStory.effect.pickTitle', { n: index + 1 })}
        className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[94vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">{t('fullStory.effect.pickTitle', { n: index + 1 })}</h2>
            <p className="text-xs text-gray-500">{t('fullStory.effect.pickDesc', { count: effects.length })}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0" aria-label={t('fullStory.effect.close')}>
            <FiX size={20} />
          </button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          {/* Preview */}
          <div className="md:w-[340px] flex-shrink-0 border-b md:border-b-0 md:border-r border-gray-200 p-4 space-y-3 bg-gray-50">
            <div className={`mx-auto rounded-lg overflow-hidden bg-black relative ${portrait ? 'w-[160px] aspect-[9/16]' : 'w-full aspect-video'}`}>
              {shownPreview ? (
                <video
                  key={shownPreview}
                  src={fullStoryService.fileUrl(id, shownPreview)}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                />
              ) : scene.image?.filename ? (
                <img
                  src={fullStoryService.fileUrl(id, scene.image.poster || scene.image.filename)}
                  alt=""
                  className="w-full h-full object-cover opacity-80"
                />
              ) : null}
              {previewing === selected && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">
                  <FiLoader className="animate-spin" size={22} />
                </div>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{effectLabel(t, current)}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {current ? current.hint : t('fullStory.effect.noneHint')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => preview()}
              disabled={!canPreview || Boolean(previewing)}
              className="btn btn-ghost w-full flex items-center justify-center gap-2 text-sm disabled:opacity-50"
            >
              {previewing === selected ? <FiLoader className="animate-spin" size={14} /> : <FiPlay size={14} />}
              {shownPreview ? t('fullStory.effect.previewAgain') : t('fullStory.effect.preview')}
            </button>
            {!canPreview && <p className="text-xs text-gray-400">{t('fullStory.effect.noPicture')}</p>}
            <p className="text-[11px] text-gray-400">{t('fullStory.effect.tip')}</p>
          </div>

          {/* Catalogue */}
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="p-3 space-y-2 border-b border-gray-100">
              <div className="relative">
                <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('fullStory.effect.search')}
                  className="input w-full pl-9 text-sm py-1.5"
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setCategory('')}
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium ${!category ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 text-gray-600 hover:border-primary-300'}`}
                >
                  {t('fullStory.effect.all')} <span className={!category ? 'text-white' : 'text-gray-400'}>{effects.length}</span>
                </button>
                {Object.entries(categories).map(([catId, label]) => (
                  <button
                    key={catId}
                    type="button"
                    onClick={() => setCategory(category === catId ? '' : catId)}
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium ${category === catId ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 text-gray-600 hover:border-primary-300'}`}
                  >
                    {categoryLabel(t, catId, label)} <span className={category === catId ? 'text-white' : 'text-gray-400'}>{counts[catId] || 0}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {!search && !category && (
                  <EffectCard
                    label={t('fullStory.effect.none')}
                    hint={t('fullStory.effect.noneHint')}
                    icon={<FiSlash size={12} />}
                    selected={selected === 'none'}
                    onSelect={() => setSelected('none')}
                  />
                )}
                {visible.map((e) => (
                  <EffectCard
                    key={e.id}
                    label={effectLabel(t, e)}
                    hint={e.hint}
                    tag={category ? null : categoryLabel(t, e.category, categories[e.category])}
                    selected={selected === e.id}
                    previewed={Boolean(previews[e.id])}
                    onSelect={() => setSelected(e.id)}
                    onDoubleClick={() => { setSelected(e.id); preview(e.id); }}
                  />
                ))}
              </div>
              {visible.length === 0 && (
                <p className="text-center text-sm text-gray-400 py-8">{t('fullStory.effect.noMatch')}</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200">
          <button type="button" onClick={onClose} className="btn btn-ghost">{t('admin.cancel')}</button>
          <button type="button" onClick={apply} disabled={saving} className="btn btn-primary flex items-center gap-2">
            {saving ? <FiLoader className="animate-spin" size={14} /> : <FiCheck size={14} />}
            {t('fullStory.effect.apply')}
          </button>
        </div>
      </div>
    </div>
  );
};

const EffectCard = ({ label, hint, tag, icon, selected, previewed, onSelect, onDoubleClick }) => (
  <button
    type="button"
    onClick={onSelect}
    onDoubleClick={onDoubleClick}
    aria-pressed={selected}
    className={`text-left rounded-lg border px-3 py-2 transition-colors ${selected ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'}`}
  >
    <span className="flex items-center gap-1.5">
      {icon}
      <span className="text-sm font-medium text-gray-900 truncate">{label}</span>
      {previewed && <FiPlay size={10} className="text-primary-500 flex-shrink-0" />}
      {selected && <FiCheck size={13} className="ml-auto text-primary-600 flex-shrink-0" />}
    </span>
    <span className="block text-[11px] leading-snug text-gray-500 mt-0.5 line-clamp-2">{hint}</span>
    {tag && <span className="block text-[10px] uppercase tracking-wide text-gray-400 mt-1">{tag}</span>}
  </button>
);
